import React, { useState } from 'react';
import { observer } from 'mobx-react';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useClientStore } from 'apps/hooks';

import { FaRegCircle } from 'react-icons/fa';
import Icon from 'components/Icon';
import { Heading, Box, SvgIcon, Divisor } from 'components';
import {
  Close,
  CylinderIcon,
  NurbsIcon,
  PlaneIcon,
  SphereIcon,
  FacesIcon,
  TorusIcon,
  EdgeIcon,
  SingleLineIcon,
  EllipseIcon,
  ConeIcon,
} from 'icons';

import { themes } from 'styles/themes/wikifactoryNew';

import {
  Navigation,
  RowWrapper,
  NavItem,
  NameWrapper,
  GeometryStyled,
  GeometryList,
  GeometryName,
  SectionsListStyled,
  SectionStyled,
  TotalGeometries,
  TotalSubGeometries,
} from './geometry.styled';
import { UiModes } from '../../config';

const GeometryIconMap = {
  Cylinder: <CylinderIcon />,
  Cone: <ConeIcon />,
  Nurbs: <NurbsIcon />,
  Sphere: <SphereIcon />,
  Torus: <TorusIcon />,
  Plane: <PlaneIcon />,
  Circle: <FaRegCircle />,
  Line: <SingleLineIcon />,
  Ellipse: <EllipseIcon />,
};

const Geometry = observer(function _Geometry() {
  const viewerControl = useViewerControl();
  const clientStore = useClientStore();

  const currCamera = viewerControl.viewer?.settings.CurrentCamera();
  const instance = currCamera?.GetInstance();
  const brep = instance?.GetBody()?.GetBrep();

  const [showingFaces, setShowingFaces] = useState(() => true);

  const getColumns = () => {
    // here we compute the number of columns for given props.size (width)
    return 5;
  };

  const getCadItem = (faceIx, loopIx, edgeIx) => {
    if (showingFaces) {
      // face
      return brep ? brep.GetFace(faceIx) : null;
    }

    // edge (over-checked for debugging...)
    const face = brep ? brep.GetFace(faceIx) : null;
    if (face) {
      const loop = face.Loop(loopIx);
      if (loop) {
        const edge = loop.Edge(edgeIx);
        if (edge) {
          return edge;
        }
      }
    }

    return null;
  };

  const faceMap = () => {
    return brep && brep.faceCount > 0 ? JSON.parse(brep.FaceMapDump()) : null;
  };

  const edgeMap = () => {
    return brep && brep.edgeCount > 0 ? JSON.parse(brep.EdgeMapDump()) : null;
  };

  const handleCloseBtn = () => {
    viewerControl.UpdateUiMode(UiModes.collapsed);
  };

  const getNavItemCommonStyle = isSelected => ({
    width: '50%',
    justifyContent: 'center',
    borderBottom: 'unset',
    background: isSelected ? themes.color.wfGrey100 : 'transparent',
  });

  return (
    <>
      {clientStore.isMobile && (
        <>
          <Box
            p={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <Heading size='medium'>BREP browser</Heading>
            <SvgIcon icon={<Close />} size='large' onClick={handleCloseBtn} />
          </Box>
          <Divisor sx={{ marginBottom: 8 }} />
        </>
      )}
      <Navigation data-testid='CADPage-navs'>
        <RowWrapper>
          <NavItem
            key='faces'
            isSelected={showingFaces}
            style={getNavItemCommonStyle(showingFaces)}
            onClick={() => setShowingFaces(true)}
          >
            <FacesIcon />
            <NameWrapper width='unset'>Faces</NameWrapper>
          </NavItem>
          <NavItem
            key='edges'
            isSelected={!showingFaces}
            style={getNavItemCommonStyle(!showingFaces)}
            onClick={() => setShowingFaces(false)}
          >
            <EdgeIcon />
            <NameWrapper width='unset'>Edges</NameWrapper>
          </NavItem>
        </RowWrapper>
      </Navigation>

      {/* faces or edges */}
      {showingFaces ? (
        <GeometryStyled>
          {faceMap() &&
            Object.keys(faceMap()).map((faceType, group) => (
              <GeometryList key={`faceGroup_${group}`}>
                <li>
                  <GeometryName>{`${faceType} `} </GeometryName>
                  <TotalGeometries>
                    {faceMap()[faceType].length}
                  </TotalGeometries>
                  <SectionsListStyled cols={getColumns()}>
                    {faceMap()[faceType].map((faceIx, i) => (
                      <GeometryItem
                        key={i}
                        ordinal={i}
                        geoType={faceType}
                        cadItem={getCadItem(faceIx)}
                      />
                    ))}
                  </SectionsListStyled>
                </li>
              </GeometryList>
            ))}
        </GeometryStyled>
      ) : (
        <GeometryStyled>
          {edgeMap() &&
            Object.keys(edgeMap()).map((edgeType, group) => (
              <GeometryList key={`edgeGroup_${group}`}>
                <li>
                  <GeometryName>{`${edgeType} `} </GeometryName>
                  <TotalGeometries>
                    {edgeMap()[edgeType].length}
                  </TotalGeometries>
                  <SectionsListStyled cols={getColumns()}>
                    {edgeMap()[edgeType].map((ix, i) => (
                      <GeometryItem
                        key={i}
                        ordinal={i}
                        geoType={edgeType}
                        cadItem={getCadItem(ix[0], ix[1], ix[2])}
                      />
                    ))}
                  </SectionsListStyled>
                </li>
              </GeometryList>
            ))}
        </GeometryStyled>
      )}
    </>
  );
});

export default Geometry;

const GeometryItem = observer(function _GeometryItem(props) {
  const viewerControl = useViewerControl();

  const selManager = viewerControl?.viewer?.selectionManager;
  const camera = viewerControl?.viewer?.settings.CurrentCamera();
  const { cadItem, geoType, ordinal } = props;

  // for auto-scroll...
  const nodeRef = () => {
    return React.createRef();
  };

  const getGeoTarget = () => {
    if (cadItem) {
      const tgt = cadItem.GetSelfTargetItem();
      tgt.instanceHitIx = camera?.GetInstanceIndex();
      return tgt;
    }
    return null;
  };

  const getPartnerGeoTarget = () => {
    if (cadItem) {
      const tgt = cadItem.GetPartnerTargetItem();
      if (tgt) {
        tgt.instanceHitIx = camera?.GetInstanceIndex();
        return tgt;
      }
    }
    return null;
  };

  const isHighlighted = () => {
    if (selManager.HighlightCount() === 0) return false;
    if (!cadItem) return false;

    const tgt = getGeoTarget();
    if (!tgt || tgt.faceHitIx < 0) return false;

    const highIx = selManager?.ContainsHighlightItemTarget(tgt._wasm);
    if (highIx >= 0) {
      return true;
    }

    if (cadItem.IsBrepEdge()) {
      // try partner
      const partnerTgt = getPartnerGeoTarget();
      if (!partnerTgt) return false;

      const partnerIx = selManager?.ContainsHighlightItemTarget(
        partnerTgt._wasm,
      );
      if (partnerIx >= 0) {
        return true;
      }
    }

    return false;
  };

  const isSelected = () => {
    if (selManager.GetSelectionCount() === 0) return false;
    if (!cadItem) return false;

    const tgt = getGeoTarget();
    if (!tgt) return false;

    const selIx = selManager?.ContainsSelectionItemTarget(tgt._wasm);
    if (selIx >= 0) {
      return true;
    }

    if (cadItem.IsBrepEdge()) {
      // try partner
      const partnerTgt = getPartnerGeoTarget();
      if (!partnerTgt) return false;

      const partnerIx = selManager?.ContainsSelectionItemTarget(
        partnerTgt._wasm,
      );
      if (partnerIx >= 0) {
        return true;
      }
    }

    return false;
  };

  const selectCadItem = () => {
    if (selManager) {
      if (cadItem) {
        const selTarget = getGeoTarget();
        if (selTarget) {
          if (!isSelected()) {
            // SELECT
            selManager.AddSelection(selTarget._wasm);
          } else {
            // UNSELECT
            selManager.DeleteSelectionItem(selTarget._wasm);
            if (cadItem.IsBrepEdge()) {
              // delete partner too
              const selPartnerTarget = getPartnerGeoTarget();
              if (selPartnerTarget) {
                selManager.DeleteSelectionItem(selPartnerTarget._wasm);
              }
            }
          }
        }
      }
    }
  };

  const highlightCadItem = () => {
    if (selManager) {
      if (cadItem) {
        const selTarget = getGeoTarget();
        if (selTarget) {
          selManager.AddHighlight(selTarget._wasm);
        }
      }
    }
  };

  const clearHighlight = () => {
    if (selManager) {
      selManager.ClearHighlight();
    }
  };

  return (
    <SectionStyled
      ref={nodeRef}
      selected={isSelected()}
      highlighted={isHighlighted()}
      onClick={selectCadItem}
      onMouseEnter={highlightCadItem}
      onMouseLeave={clearHighlight}
    >
      <div className='flex items-center w-full h-full'>
        {GeometryIconMap[geoType] || (
          <Icon
            name={geoType.toLowerCase()}
            size='xsmall'
            viewBox='0 0 40 40'
          />
        )}
        <TotalSubGeometries selected={isSelected()}>
          {1 + ordinal}
        </TotalSubGeometries>
      </div>
    </SectionStyled>
  );
});
